import { useMemo, useState } from 'react';
import { Card } from '@clay-run/glaze/card';
import { Text } from '@clay-run/glaze/text';
import { ComboboxField } from '@clay-run/glaze/combobox-field';
import { ComboboxOption } from '@clay-run/glaze/combobox';
import { DebouncedSearchInput } from '@clay-run/glaze/debounced-search-input';
import { ProgressBar } from '@clay-run/glaze/progress-bar';
import { SkeletonText } from '@clay-run/glaze/skeleton';
import { campaignWhenAndWhere, type Campaign } from '../campaigns';
import { ScanStatusSchema, SCAN_STATUS_LABEL, isUnfinishedScan, type Scan, type ScanStatus } from '../scan';
import { applyScanFilters, effectiveSubmitter, type ScansFilters } from '../scans-url-state';
import { isReadyDraft } from '../bulk-submit';
import { ScanRow } from './ScanList';
import BulkSubmitDrafts from './BulkSubmitDrafts';

/**
 * The shared "scans list" card body (QA-fix batch, Fix 1) — search input +
 * status filter (+ optional submitter/campaign filters) + count header +
 * batch-progress bar + the `ScanRow` list. Extracted out of `ScansPage.tsx`
 * (where every one of these pieces originated) so `Home.tsx`'s "Scans" card
 * can reuse the EXACT same filtering/rendering rather than re-implement a
 * second, drifted copy: before this fix Home hardcoded a fixed
 * `isUnfinishedScan`-only view with no way to see a `synced`/`submitting`
 * scan again once it left that status.
 *
 * Both callers own their OWN filter state (`filters` + `onFiltersChange`)
 * rather than this component owning any of it: `ScansPage.tsx` backs its
 * filters with `useQueryStates` (URL-shareable, per the deep-linking
 * standard), `Home.tsx` backs its with plain local `useState` (not a
 * distinct URL route) — this component is agnostic to which. Filtering
 * itself (`applyScanFilters`) and the submitter/status/campaign combobox
 * items are identical between the two surfaces; only which combos RENDER
 * differs (`showSubmitterFilter`/`showCampaignFilter`) — Home shows just
 * status + search, since "mine" is implicit and there's no per-surface
 * campaign scope to filter by.
 *
 * Also renders the "Submit N ready drafts" bulk button
 * (`BulkSubmitDrafts.tsx`, plans/2026-09-18-001-lead-scanner-heic-bulk-submit-plan.md
 * §2) next to the scan count — scoped to the CURRENTLY FILTERED/VISIBLE list
 * and the viewer's own rows, so both callers get it for free with no prop
 * changes of their own.
 */

export interface ScanListBatchProgress {
  total: number;
  resolved: number;
  done: boolean;
}

interface ScanListCardProps {
  /** The unfiltered fetched list — filtering happens in here via `filters`. */
  scans: Scan[];
  isLoading: boolean;
  me: string | null;
  filters: ScansFilters;
  /** Called with only the filter keys that changed — the caller merges it
   *  into its own filter state (a `setState` merge, or a nuqs `setParams`
   *  call — both accept a partial patch the same way). */
  onFiltersChange: (patch: Partial<ScansFilters>) => void;
  /** Used for the campaign-filter combo's options (when shown) AND for
   *  `applyScanFilters`'s `q`-search-by-event-name, regardless of whether
   *  the campaign combo itself is shown. */
  campaigns: Campaign[];
  /** @default false — ScansPage only; Home's "mine" is implicit. */
  showSubmitterFilter?: boolean;
  /** @default false — ScansPage only; Home has no cross-campaign scope. */
  showCampaignFilter?: boolean;
  onOpenScan: (id: string) => void;
  onDeleteScan: (id: string) => void;
  batchProgress: ScanListBatchProgress;
  /** Highlights the open row in the wide two-pane layout — ScansPage only. */
  isWide?: boolean;
  selectedScanId?: string | null;
}

export default function ScanListCard({
  scans,
  isLoading,
  me,
  filters,
  onFiltersChange,
  campaigns,
  showSubmitterFilter = false,
  showCampaignFilter = false,
  onOpenScan,
  onDeleteScan,
  batchProgress,
  isWide = false,
  selectedScanId = null,
}: ScanListCardProps) {
  const campaignNames = useMemo(() => new Map(campaigns.map((c) => [c.id, c.name])), [campaigns]);

  const filteredScans = useMemo(
    () => (me === null ? [] : applyScanFilters(scans, filters, me, campaignNames)),
    // `filters` is a fresh object every render for either caller (built from
    // `params` or from local state) — listing its five scalar fields (plus
    // the derived `campaignNames` map) instead of the object itself is the
    // real dependency list.
    [scans, me, filters.mine, filters.rep, filters.status, filters.campaign, filters.q, campaignNames],
  );

  // "Submit N ready drafts" scope (Decision 1 of the bulk-submit plan): the
  // CURRENTLY FILTERED/VISIBLE list (`filteredScans`, not the unfiltered
  // `scans`) — matches "what I'm looking at", same as the count header right
  // below — restricted to the viewer's OWN rows even when the surface shows
  // everyone's (ScansPage's "Everyone" submitter filter): one tap firing
  // Salesforce submissions under a teammate's name would be a surprising
  // side effect the single-scan Submit button doesn't have (that one is
  // always a deliberate per-row action). `error`/`needs_review` rows are
  // excluded on purpose too — `isReadyDraft` only matches `draft` — since
  // those are Retry territory and deserve a rep's eyes, not a silent resend.
  const readyDrafts = useMemo(
    () => filteredScans.filter((s) => s.scannedByEmail === me && isReadyDraft(s)),
    [filteredScans, me],
  );

  // The single filtered-out-of-"mine" submitter, or `null` for "Everyone" —
  // NOT per-row: comparing to `me` (the viewer) once, not to each row's own
  // `scannedByEmail`. On Home, `filters.mine` is always `true` and
  // `filters.rep` is always `null`, so `submitter` always resolves to `me`
  // and this is always `false` there.
  const submitter = effectiveSubmitter(filters.mine, filters.rep, me ?? '');
  const showScannedBy = submitter !== me;

  const submitterItems = useMemo(() => {
    const distinctEmails = Array.from(new Set(scans.map((s) => s.scannedByEmail))).sort((a, b) => a.localeCompare(b));
    return [
      { key: 'mine', label: 'My scans', mine: true, rep: null as string | null },
      { key: 'everyone', label: 'Everyone', mine: false, rep: null as string | null },
      ...distinctEmails.map((email) => ({ key: email, label: email, mine: false, rep: email })),
    ];
  }, [scans]);
  const submitterSelectedKey = filters.rep ?? (filters.mine ? 'mine' : 'everyone');

  const statusItems = useMemo(
    () => [
      { key: 'all', label: 'All statuses', value: null as ScanStatus | null },
      ...ScanStatusSchema.options.map((status) => ({ key: status, label: SCAN_STATUS_LABEL[status], value: status })),
    ],
    [],
  );
  const statusSelectedKey = filters.status ?? 'all';

  const campaignItems = useMemo(
    () => [
      { key: 'all', label: 'All campaigns', value: null as string | null },
      ...campaigns.map((c) => ({ key: c.id, label: `${c.name} — ${campaignWhenAndWhere(c)}`, value: c.id })),
    ],
    [campaigns],
  );
  const campaignSelectedKey = filters.campaign ?? 'all';

  // ─── Filter-combobox typed narrowing (live-QA round-3 bug 3, ported
  // verbatim from `ScansPage.tsx`) ────────────────────────────────────────
  const [submitterFilterQuery, setSubmitterFilterQuery] = useState('');
  const trimmedSubmitterFilterQuery = submitterFilterQuery.trim().toLowerCase();
  const visibleSubmitterItems = trimmedSubmitterFilterQuery
    ? submitterItems.filter((i) => i.label.toLowerCase().includes(trimmedSubmitterFilterQuery))
    : submitterItems;

  const [statusFilterQuery, setStatusFilterQuery] = useState('');
  const trimmedStatusFilterQuery = statusFilterQuery.trim().toLowerCase();
  const visibleStatusItems = trimmedStatusFilterQuery
    ? statusItems.filter((i) => i.label.toLowerCase().includes(trimmedStatusFilterQuery))
    : statusItems;

  const [campaignFilterQuery, setCampaignFilterQuery] = useState('');
  const trimmedCampaignFilterQuery = campaignFilterQuery.trim().toLowerCase();
  const visibleCampaignItems = trimmedCampaignFilterQuery
    ? campaignItems.filter((i) => i.label.toLowerCase().includes(trimmedCampaignFilterQuery))
    : campaignItems;

  return (
    <Card padded className="flex flex-col gap-3">
      <DebouncedSearchInput
        aria-label="Search by name or event"
        placeholder="Search by name or event…"
        initialValue={filters.q}
        onSearch={(value) => onFiltersChange({ q: value })}
        onClear={() => onFiltersChange({ q: '' })}
      />
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        {/* `ComboboxField`'s own `className` prop lands on the inner trigger,
            not the outer `FormControl`/`Field` wrapper that's the actual flex
            item here — that wrapper hardcodes `w-full` with no override hook
            (`node_modules/@clay-run/glaze/src/form-control.tsx`). A `flex-1
            min-w-0` div around each field is what actually makes them share
            the row instead of each claiming 100% and overflowing it. */}
        {showSubmitterFilter && (
          <div className="flex-1 min-w-0">
            <ComboboxField
              variant="popover"
              aria-label="Filter by submitter"
              searchPlaceholder="Search submitters…"
              items={visibleSubmitterItems}
              itemToStringLabel={(i: (typeof submitterItems)[number]) => i.label}
              value={submitterItems.find((i) => i.key === submitterSelectedKey) ?? null}
              onValueChange={(i) => i && onFiltersChange({ mine: i.mine, rep: i.rep })}
              inputValue={submitterFilterQuery}
              onInputValueChange={setSubmitterFilterQuery}
              hasClearButton={false}
              by="key"
            >
              {visibleSubmitterItems.map((i) => (
                <ComboboxOption key={i.key} value={i}>
                  {i.label}
                </ComboboxOption>
              ))}
            </ComboboxField>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <ComboboxField
            variant="popover"
            aria-label="Filter by status"
            searchPlaceholder="Search statuses…"
            items={visibleStatusItems}
            itemToStringLabel={(i: (typeof statusItems)[number]) => i.label}
            value={statusItems.find((i) => i.key === statusSelectedKey) ?? null}
            onValueChange={(i) => i && onFiltersChange({ status: i.value })}
            inputValue={statusFilterQuery}
            onInputValueChange={setStatusFilterQuery}
            hasClearButton={false}
            by="key"
          >
            {visibleStatusItems.map((i) => (
              <ComboboxOption key={i.key} value={i}>
                {i.label}
              </ComboboxOption>
            ))}
          </ComboboxField>
        </div>
        {showCampaignFilter && (
          <div className="flex-1 min-w-0">
            <ComboboxField
              variant="popover"
              aria-label="Filter by campaign"
              searchPlaceholder="Search campaigns…"
              items={visibleCampaignItems}
              itemToStringLabel={(i: (typeof campaignItems)[number]) => i.label}
              // Found in the UNfiltered list so the trigger label survives an
              // active query (`campaignItems`, not `visibleCampaignItems`).
              value={campaignItems.find((i) => i.key === campaignSelectedKey) ?? null}
              onValueChange={(i) => i && onFiltersChange({ campaign: i.value })}
              inputValue={campaignFilterQuery}
              onInputValueChange={setCampaignFilterQuery}
              hasClearButton={false}
              by="key"
            >
              {visibleCampaignItems.map((i) => (
                <ComboboxOption key={i.key} value={i}>
                  {i.label}
                </ComboboxOption>
              ))}
            </ComboboxField>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2">
        <Text size="sm" weight="semibold">
          Scans
        </Text>
        <div className="flex items-center gap-2">
          <Text size="xs" variant="tertiary">
            {filteredScans.length} of {scans.length}
          </Text>
          <BulkSubmitDrafts scans={readyDrafts} />
        </div>
      </div>

      {batchProgress.total > 1 && !batchProgress.done && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <Text size="sm" weight="semibold">
              Reading badges
            </Text>
            <Text size="xs" variant="tertiary">
              {batchProgress.resolved} of {batchProgress.total}
            </Text>
          </div>
          <ProgressBar percentage={(batchProgress.resolved / batchProgress.total) * 100} />
        </div>
      )}

      {isLoading ? (
        <div className="flex flex-col gap-2">
          <SkeletonText size="sm" />
          <SkeletonText size="sm" />
          <SkeletonText size="sm" />
        </div>
      ) : filteredScans.length === 0 ? (
        <Text size="sm" variant="tertiary">
          No scans match these filters.
        </Text>
      ) : (
        <div className="flex flex-col gap-2">
          {filteredScans.map((scan) => {
            const deletableStatus = isUnfinishedScan(scan);
            return (
              <ScanRow
                key={scan.id}
                scan={scan}
                onOpen={onOpenScan}
                selected={isWide && scan.id === selectedScanId}
                showScannedBy={showScannedBy}
                currentUserEmail={me}
                onDelete={deletableStatus ? onDeleteScan : undefined}
              />
            );
          })}
        </div>
      )}
    </Card>
  );
}
